import { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import DOMPurify from 'dompurify';
import { useTypewriterEffect } from '@aimsos/hooks';

/**
 * Normalize message content to string (handles API object/array formats — same idea as ann-web-app).
 * @param {unknown} content
 */
export function normalizeChatMessageContent(content) {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    const textItems = content.filter((item) => item?.type === 'text' && item?.text);
    if (textItems.length > 0) return textItems.map((item) => item.text).join('\n\n');
    const fallback = content.find((item) => item?.text || item?.content || item?.message);
    return fallback ? fallback.text || fallback.content || fallback.message : '';
  }
  if (content && typeof content === 'object') {
    const o = /** @type {{ message?: string, content?: string, text?: string, formattedText?: string }} */ (
      content
    );
    return o.message || o.content || o.text || o.formattedText || '';
  }
  return '';
}

function isLikelyHtml(str) {
  if (typeof str !== 'string' || !str.trim()) return false;
  if (typeof document === 'undefined') return /<\/?[a-z][\s\S]*>/i.test(str);
  const doc = new DOMParser().parseFromString(str, 'text/html');
  return Array.from(doc.body.childNodes).some((node) => node.nodeType === 1);
}

const createPurifiedMarkup = (html) => ({
  __html: DOMPurify.sanitize(html, {
    ALLOWED_ATTR: ['class', 'id', 'style', 'href', 'target', 'rel', 'src', 'alt', 'title'],
    ALLOWED_TAGS: [
      'p',
      'br',
      'b',
      'i',
      'em',
      'strong',
      'a',
      'img',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'ul',
      'ol',
      'li',
      'blockquote',
      'code',
      'pre',
      'hr',
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
      'div',
      'span',
      'del',
      'mark',
    ],
    ALLOWED_CSS_PROPERTIES: [
      'color',
      'background-color',
      'font-weight',
      'font-style',
      'text-decoration',
      'text-align',
      'font-size',
    ],
  }),
});

const REHYPE_SANITIZE_OPTIONS = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []), 'mark'],
  attributes: {
    ...(defaultSchema.attributes || {}),
    a: [...((defaultSchema.attributes && defaultSchema.attributes.a) || []), 'target', 'rel'],
    img: [...((defaultSchema.attributes && defaultSchema.attributes.img) || []), 'src', 'alt', 'title'],
    mark: ['className', 'class'],
    '*': [...((defaultSchema.attributes && defaultSchema.attributes['*']) || []), 'className', 'class'],
  },
};

const REHYPE_PLUGINS = [rehypeRaw, [rehypeSanitize, REHYPE_SANITIZE_OPTIONS]];

const MARKDOWN_COMPONENTS = {
  table: ({ node: _n, ...props }) => (
    <div className="my-3 max-w-full overflow-x-auto scrollbar-aimsos">
      <table
        className="markdown-table w-full border-collapse border border-edge text-sm dark:border-edge"
        {...props}
      />
    </div>
  ),
  tr: ({ node: _n, ...props }) => <tr className="markdown-tr border-b border-edge dark:border-edge" {...props} />,
  th: ({ node: _n, ...props }) => (
    <th
      className="markdown-th border border-edge bg-surface-muted px-3 py-2 text-left font-semibold dark:border-edge"
      {...props}
    />
  ),
  td: ({ node: _n, ...props }) => (
    <td className="markdown-td border border-edge px-3 py-2 align-top dark:border-edge" {...props} />
  ),
  mark: ({ node: _n, className, children, ...props }) => {
    const classStr =
      typeof className === 'string'
        ? className
        : Array.isArray(className)
          ? className.filter(Boolean).join(' ')
          : '';
    return (
      <mark className={classStr || undefined} {...props}>
        {children}
      </mark>
    );
  },
  a: ({ node: _n, href, children, ...props }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="gfm-link font-medium text-brand-600 underline decoration-brand-600/40 underline-offset-2 hover:text-brand-700 break-words [overflow-wrap:anywhere]"
      {...props}
    >
      {children ?? href}
    </a>
  ),
};

const markdownShellClass =
  [
    'chat-message-markdown min-w-0 max-w-full flex-1 text-left text-sm leading-relaxed text-content break-words [overflow-wrap:anywhere]',
    '[&_p]:mb-2 [&_p:last-child]:mb-0',
    '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5',
    '[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
    '[&_li]:my-0.5',
    '[&_strong]:font-semibold',
    '[&_code]:rounded [&_code]:bg-surface-hover [&_code]:px-1 [&_code]:font-mono [&_code]:text-[0.875em]',
    '[&_pre]:scrollbar-aimsos-auto [&_pre]:my-2 [&_pre]:max-h-[min(24rem,50vh)] [&_pre]:overflow-auto [&_pre]:rounded-lg [&_pre]:border [&_pre]:border-edge [&_pre]:bg-surface-muted [&_pre]:p-3',
    '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
    '[&_blockquote]:my-2 [&_blockquote]:border-l-4 [&_blockquote]:border-brand-400 [&_blockquote]:pl-3 [&_blockquote]:text-content-secondary',
    '[&_h1]:mb-2 [&_h1]:mt-3 [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:first:mt-0',
    '[&_h2]:mb-2 [&_h2]:mt-3 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:first:mt-0',
    '[&_h3]:mb-1 [&_h3]:mt-2 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:first:mt-0',
    '[&_hr]:my-4 [&_hr]:border-edge',
    '[&_table]:text-content',
  ].join(' ');

const TypewriterCursor = () => (
  <span
    className="ml-0.5 inline-block align-baseline text-content-muted animate-chat-typewriter-blink"
    aria-hidden
  >
    ▋
  </span>
);

/**
 * Renders assistant markdown (GFM + sanitized raw HTML), or full HTML bubbles when the model returns HTML.
 * When `highlightMarkdown` is set, search highlights are shown and typewriter is disabled (ann-web-app).
 * When `isStreaming` is true on the last agent bubble, uses {@link useTypewriterEffect} like ann `AgentMessageContent`.
 *
 * @param {object} props
 * @param {unknown} props.content
 * @param {string|null} [props.highlightMarkdown]
 * @param {boolean} [props.isStreaming]
 * @param {(typing: boolean) => void} [props.onTypingStateChange]
 */
const ChatMessageMarkdown = ({
  content,
  highlightMarkdown = null,
  isStreaming = false,
  onTypingStateChange,
}) => {
  const isSearchHighlight = highlightMarkdown != null;
  const normalized = normalizeChatMessageContent(content) || '';
  const { displayedText, isTyping } = useTypewriterEffect(
    isSearchHighlight ? '' : normalized,
    isSearchHighlight ? false : isStreaming,
  );

  useEffect(() => {
    onTypingStateChange?.(!isSearchHighlight && isTyping);
  }, [isSearchHighlight, isTyping, onTypingStateChange]);

  if (isSearchHighlight) {
    return (
      <div className={`markdown-content ${markdownShellClass}`}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={REHYPE_PLUGINS}
          components={MARKDOWN_COMPONENTS}
        >
          {highlightMarkdown}
        </ReactMarkdown>
      </div>
    );
  }

  const textToRender = displayedText;

  if (!textToRender) {
    return (
      <div className={`markdown-content ${markdownShellClass}`}>
        {isTyping ? <TypewriterCursor /> : '\u00a0'}
      </div>
    );
  }

  if (isLikelyHtml(textToRender)) {
    return (
      <div className={`markdown-content inline-block max-w-full ${markdownShellClass}`}>
        <div
          className="html-content scrollbar-aimsos-auto max-w-full overflow-x-auto"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={createPurifiedMarkup(textToRender)}
          role="text"
        />
        {isTyping ? <TypewriterCursor /> : null}
      </div>
    );
  }

  return (
    <div className={`markdown-content inline-block max-w-full ${markdownShellClass}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={REHYPE_PLUGINS}
        components={MARKDOWN_COMPONENTS}
      >
        {textToRender}
      </ReactMarkdown>
      {isTyping ? <TypewriterCursor /> : null}
    </div>
  );
};

export default ChatMessageMarkdown;
